import { useLocation } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { DateTime } from 'luxon';

import { useAppContext } from '@app/contexts';
import {
  UserSearchDto,
  useSaveRecentInstancesMutation,
  useSaveRecentSearchesMutation,
} from '@app/hooks';
import { addEntryToDashboardList } from '@app/utils';
import {
  searchInterface,
  ShapeWithTotalRecords,
  storageKeys,
} from '@app/constants';

import {
  SearchInstanceDto,
  SearchOptionType,
  SearchState,
} from '../../constants';

export type SearchHistoryEntry = ShapeWithTotalRecords<{
  searchInterface: string;
  timestamp: string;
  searchType: SearchOptionType;
  requestParams?: string;
  id?: string;
}> &
  SearchState;

export interface RecordsWithTimestamp {
  instanceId: string;
  timestamp: string;
  id?: string;
}

export type TimestampedRecordWithData = RecordsWithTimestamp &
  SearchInstanceDto;

export const useRecentActivityHistory = () => {
  const { isLoggedIn } = useAppContext();
  const [searchHistory, setSearchHistory] = useSessionStorage<UserSearchDto[]>(
    storageKeys.searchHistory,
    []
  );
  const [viewedRecords, setViewedRecords] = useSessionStorage<
    RecordsWithTimestamp[]
  >(storageKeys.viewedRecords, []);
  const saveRecentInstanceMutation = useSaveRecentInstancesMutation();
  const saveRecentSearchMutation = useSaveRecentSearchesMutation();
  const { search: urlQuery } = useLocation();

  const addSearchHistoryEntry = (entry: SearchState, totalRecords: number) => {
    const searchEntry = {
      query: entry.query as string,
      requestParams: urlQuery,
      resultCount: totalRecords,
      searchType: entry.option,
      interface: searchInterface,
      date: DateTime.now().toISO(),
    };

    if (isLoggedIn) {
      saveRecentSearchMutation.mutate({ searches: [searchEntry] });
    } else {
      setSearchHistory(addEntryToDashboardList(searchEntry, searchHistory));
    }
  };

  const addViewedRecord = (record: RecordsWithTimestamp) => {
    if (isLoggedIn) {
      saveRecentInstanceMutation.mutate({
        viewedInstances: [{ instanceId: record.instanceId }],
      });
    } else {
      setViewedRecords(addEntryToDashboardList(record, viewedRecords));
    }
  };

  return {
    addSearchHistoryEntry,
    addViewedRecord,
  };
};
